// components/CardProductos.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  TableFooter,
  LinearProgress
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  LocalShipping as LocalShippingIcon,
  Weight as WeightIcon
} from '@mui/icons-material';
import moment from 'moment';
import { getResumenProductosSucursal } from '../../services/productos.service';
import CardExpandible from '../CardExpandible';

const formatNumber = (value) => {
  if (!value && value !== 0) return '';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';
  
  const [entero, decimal] = numValue.toFixed(2).split('.');
  const enteroFormateado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${enteroFormateado},${decimal}`;
};

const CardProductos = ({ 
  sucursal,
  desde, 
  hasta, 
  loading = false,
  triggerRecarga = 0
}) => {
  // Estados
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState({});

  // Extraer datos de sucursal
  const sucursalId = sucursal?.id;
  const sucursalNombre = sucursal?.nombre;
  const mpStoreId = sucursal?.mp_store_id;

  // Efecto para cargar datos
  useEffect(() => {
    if (sucursalId && desde && hasta) {
      cargarDatos();
    }
  }, [sucursalId, desde, hasta, triggerRecarga]);

  const cargarDatos = async () => {
    setLoadingData(true);
    setError(null);
    
    try {
      const response = await getResumenProductosSucursal(sucursalId, desde, hasta);
      
      
      //  Verificar si la respuesta es un array
      if (Array.isArray(response)) {
        setData(response);
        setPage(0);
      } 
      //  Si la respuesta tiene la estructura { success, data }
      else if (response.success) {
        setData(response.data || []);
        setPage(0);
      } 
      //  Si hay error
      else {
        setError(response.error || 'Error al cargar los datos');
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
      setError(error.message || 'Error al cargar los datos de productos');
    } finally {
      setLoadingData(false);
    }
  };

  // Manejadores de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRows({});
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows({});
  };

  const toggleExpandRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Calcular totales
  const totalCantidad = data.reduce((sum, item) => sum + (item.cantidad_vendida || 0), 0);
  const totalPeso = data.reduce((sum, item) => sum + (item.peso_total_vendido || 0), 0);

  // Obtener el porcentaje de ventas para la barra de progreso
  const getPorcentaje = (cantidad) => {
    if (totalCantidad === 0) return 0;
    return (cantidad / totalCantidad) * 100;
  };

  // Obtener color según cantidad vendida
  const getColorByCantidad = (cantidad) => {
    if (cantidad > 100) return '#2e7d32';
    if (cantidad > 50) return '#1976d2';
    if (cantidad > 20) return '#ed6c02';
    return '#9e9e9e';
  };

  // Paginación
  const datosPaginados = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  // Loading
  if (loading || loadingData) {
    return (
      <CardExpandible title="Productos Vendidos" icon={InventoryIcon}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} sx={{ color: '#757575' }} />
        </Box>
      </CardExpandible>
    );
  }

  // Validaciones
  if (!sucursalId || !desde || !hasta) {
    let mensaje = '';
    if (!sucursalId) mensaje = 'Selecciona una sucursal';
    else if (!desde || !hasta) mensaje = 'Selecciona un rango de fechas';
    
    return (
      <CardExpandible title="Productos Vendidos" icon={InventoryIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {mensaje}
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  if (error) {
    return (
      <CardExpandible title="Productos Vendidos" icon={InventoryIcon}>
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </CardExpandible>
    );
  }

  if (!data || data.length === 0) {
    return (
      <CardExpandible title="Productos Vendidos" icon={InventoryIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No hay productos vendidos en este período
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  return (
    <CardExpandible 
      title="Productos Vendidos" 
      icon={InventoryIcon}
      subtitle={sucursalNombre ? `Sucursal: ${sucursalNombre} · ` : ''}
    >
      <Box>
        {/* Resumen de totales */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={4}>
            <Box sx={{ 
              // p: 1.5, 
              bgcolor: '#e8f5e9', 
              borderRadius: 2,
              border: '1px solid #c8e6c9',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '1.1rem' }}>
                {totalCantidad}
              </Typography>
              <Typography variant="caption" color="textSecondary">Total Unidades</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={4}>
            <Box sx={{ 
              // p: 1.5, 
              bgcolor: '#e3f2fd', 
              borderRadius: 2,
              border: '1px solid #bbdefb',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', fontSize: '1.1rem' }}>
                {data.length}
              </Typography>
              <Typography variant="caption" color="textSecondary">Productos Diferentes</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={4}>
            <Box sx={{ 
              // p: 1.5, 
              bgcolor: '#fff3e0', 
              borderRadius: 2,
              border: '1px solid #ffe0b2',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#ed6c02', fontSize: '1.1rem' }}>
                {formatNumber(totalPeso)} kg
              </Typography>
              <Typography variant="caption" color="textSecondary">Peso Total</Typography>
            </Box>
          </Grid>
          
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Tabla de productos */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e8e8e8' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Producto</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Cantidad</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Peso Unitario</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Peso Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datosPaginados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No hay registros para mostrar
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                datosPaginados.map((item, index) => {
                  const globalIndex = page * rowsPerPage + index;
                  const key = `producto-${globalIndex}`;
                  const porcentaje = getPorcentaje(item.cantidad_vendida || 0);
                  
                  return (
                    <React.Fragment key={key}>
                      <TableRow hover>
                        <TableCell>
                          <Chip
                            label={item.codigo || '-'}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 22,
                              bgcolor: '#f5f5f5',
                              fontFamily: 'monospace'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {item.tipo_envase || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500, color: getColorByCantidad(item.cantidad_vendida) }}>
                          {item.cantidad_vendida || 0}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                          {item.peso ? `${item.peso} kg` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {item.peso_total_vendido ? `${formatNumber(item.peso_total_vendido)} kg` : '-'}
                        </TableCell>
                        
                      </TableRow>

                    </React.Fragment>
                  );
                })
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={7}
                  count={data.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} de ${count}`
                  }
                  sx={{
                    '.MuiTablePagination-select': {
                      fontSize: '0.8rem'
                    },
                    '.MuiTablePagination-displayedRows': {
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* Información adicional */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            {/* {mpStoreId && `MP Store ID: ${mpStoreId} · `} */}
            Sucursal ID: {sucursalId}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total productos: {data.length} · Actualizado: {moment().format('DD-MM-YYYY HH:mm')}
          </Typography>
        </Box>
      </Box>
    </CardExpandible>
  );
};

export default CardProductos;